import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { LessonPlayerArrowButton } from './lesson-player-arrow-button/lesson-player-arrow-button';
import { LessonPlayerNavbar } from './lesson-player-navbar/lesson-player-navbar';
import styles from './lesson-player-viewer.style';
import { SizeBreakpoint } from '../../../enums/size-breakpoint';
import {
  useColors,
  useLessonContext,
} from '../../../hooks/lesson-player.context';
import { useActivityContext } from '../../../hooks/use-activity.context';
import useOnLayout from '../../../hooks/use-on-layout';
import type { NavHeader } from '../../../interfaces/nav-header';
import { LessonActivitySelector } from '../../lesson-activity-selector/lesson-activity-selector.component';
import { LessonIndex } from '../../lesson-index/lesson-index';
import { LessonProgressBar } from '../../lesson-progress-bar/lesson-progress-bar';
import { NavbarIconButton } from '../../navbar-icon-button/navbar-icon-button';
import { ProgressBarHeader } from '../../progress-bar-header/progress-bar-header';
import { SlideIndicator } from '../../slide-indicator/slide-indicator.component';
import { ThemedText } from '../../themed-text';

interface LessonPlayerViewerProps {
  originalSlides: any[];
  navHeader?: NavHeader;
}

export function LessonPlayerViewer({
  originalSlides,
  navHeader,
}: LessonPlayerViewerProps) {
  const {
    indexVisible,
    hideIndex,
    toggleIndexVisibility,
    translationActive,
    toggleTranslation,
    isTranslationButtonEnabled,
    showTranslationButton,
  } = useLessonContext();

  const {
    totalSlides,
    currentIndex,
    currentActivity,
    totalActivitiesCompleted,
    completeCurrentActivity,
    goNext,
    completions,
    goToActivityWithIndex,
    goNextWithoutSteps,
    goPrevWithoutSteps,
  } = useActivityContext();

  const completionsWithoutFinishActivity = useMemo(
    () => completions.slice(0, -1),
    [completions]
  );

  const isLastSlide = currentIndex >= totalSlides;
  const actionText = !isLastSlide ? 'Next' : 'Finish';
  const [{ width: headerWidth }, onLayout] = useOnLayout();

  const changeSlide = (index: number) => {
    hideIndex();
    goToActivityWithIndex(index);
  };

  const { t } = useTranslation();
  const colors = useColors();

  return (
    <>
      {navHeader && <LessonPlayerNavbar {...navHeader} />}

      <ProgressBarHeader
        onLayout={onLayout}
        containerStyle={styles.progressHeaderContainer}
        style={styles.progressHeader}
      >
        {navHeader?.breadcrumbTitle && headerWidth > SizeBreakpoint.MD && (
          <View
            style={[
              styles.headerSection,
              styles.borderHeaderSection,
              { borderColor: colors.grayLight },
            ]}
          >
            <ThemedText style={styles.breadcrumbText}>
              {navHeader.breadcrumbTitle}
            </ThemedText>
          </View>
        )}

        <View
          style={[
            styles.headerSection,
            styles.centerHeaderSection,
            { borderColor: colors.grayLight },
          ]}
        >
          <LessonPlayerArrowButton
            onPress={goPrevWithoutSteps}
            direction="left"
          />
          <LessonProgressBar
            containerStyle={styles.progressBar}
            currentIndex={currentIndex}
            totalActivities={completionsWithoutFinishActivity.length}
            completedActivities={
              completionsWithoutFinishActivity.filter(
                (completed) => !!completed
              ).length
            }
            isIndicatorVisible={!isLastSlide}
          />
          <SlideIndicator
            current={Math.min(totalActivitiesCompleted, totalSlides)}
            total={totalSlides}
          />
          <LessonPlayerArrowButton
            onPress={goNextWithoutSteps}
            direction="right"
          />
        </View>

        {headerWidth > SizeBreakpoint.MD && (
          <View
            style={[
              styles.headerSection,
              styles.borderHeaderSection,
              styles.navSection,
              { borderColor: colors.grayLight },
            ]}
          >
            <NavbarIconButton
              titlePlacement={
                headerWidth < SizeBreakpoint.LG ? 'bottom' : 'right'
              }
              icon="list-alt"
              active={indexVisible}
              title={t('nav.activities') as string}
              onPress={toggleIndexVisibility}
            />
            {showTranslationButton && (
              <NavbarIconButton
                titlePlacement={
                  headerWidth < SizeBreakpoint.LG ? 'bottom' : 'right'
                }
                disabled={!isTranslationButtonEnabled}
                icon="language"
                active={translationActive}
                onPress={toggleTranslation}
                title={t('nav.translation') as string}
              />
            )}
          </View>
        )}
      </ProgressBarHeader>
      <View style={styles.activityView}>
        <LessonActivitySelector
          activity={currentActivity}
          completionButtonText={actionText}
          completeActivity={completeCurrentActivity}
          goNext={goNext}
          isLast={isLastSlide}
          isComplete={completions[currentIndex]}
          updateStepProgress={() => {}}
        />
      </View>
      <LessonIndex
        isFullScreen={false}
        title={navHeader?.title}
        slideNumber={currentIndex}
        subtitle={navHeader?.breadcrumbTitle}
        completionList={completions}
        slides={originalSlides}
        isVisible={indexVisible}
        changeSlide={(i) => changeSlide(i)}
      />
    </>
  );
}
